/* Optional cart add-on: its presence in the Shopify cart is the source of truth. */
class OzeraCartProtection extends HTMLElement {
  connectedCallback() {
    this.drawer = this.closest('cart-drawer');
    this.toggle = this.querySelector('[role="switch"]');
    this.info = this.querySelector('.ozera-cart-protection__info');
    this.details = this.querySelector('[data-protection-details]');
    this.error = this.querySelector('[role="alert"]');
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.toggle.addEventListener('click', () => this.setProtection(this.toggle.getAttribute('aria-checked') !== 'true'), options);
    this.info.addEventListener('click', () => {
      const expanded = this.info.getAttribute('aria-expanded') !== 'true';
      this.info.setAttribute('aria-expanded', String(expanded));
      this.details.hidden = !expanded;
    }, options);
    if (Number(this.dataset.merchandiseCount) === 0) this.setProtection(false);
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  async request(path, body) {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}${path}`, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const data = await response.json();
    if (!response.ok || data.status >= 400) throw new Error(data.description || 'Košík se nepodařilo aktualizovat. Zkuste to prosím znovu.');
    return data;
  }

  async setProtection(enabled) {
    if (OzeraCartProtection.pending) return;
    OzeraCartProtection.pending = true;
    const checkout = this.drawer.querySelector('[name="checkout"]');
    const itemControls = [...this.drawer.querySelectorAll('cart-drawer-items button, cart-drawer-items input, cart-drawer-items select')]
      .map(element => ({ element, disabled: element.disabled }));
    itemControls.forEach(({ element }) => { element.disabled = true; });
    this.drawer.setAttribute('aria-busy', 'true');
    this.toggle.disabled = true;
    this.setAttribute('aria-busy', 'true');
    this.error.hidden = true;
    if (checkout) checkout.disabled = true;
    try {
      const cart = await this.request('cart.js');
      const productId = Number(this.dataset.productId);
      const protection = cart.items.filter(item => item.product_id === productId);
      const hasMerchandise = cart.items.some(item => item.product_id !== productId);
      const sections = { sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: window.location.pathname };
      let updated;
      if (enabled && hasMerchandise && protection.length === 0) {
        updated = await this.request('cart/add.js', { items: [{ id: Number(this.dataset.variantId), quantity: 1 }], ...sections });
      } else {
        const updates = {};
        protection.forEach((item, index) => { updates[item.key] = enabled && hasMerchandise && index === 0 ? 1 : 0; });
        updated = await this.request('cart/update.js', { updates, ...sections });
      }
      if (!updated.sections?.['cart-drawer'] || !updated.sections?.['cart-icon-bubble']) {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}?sections=cart-drawer,cart-icon-bubble`, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Košík se nepodařilo zobrazit. Obnovte prosím stránku.');
        updated.sections = await response.json();
      }
      this.drawer.classList.toggle('is-empty', !hasMerchandise);
      this.drawer.renderContents(updated, true);
      const newToggle = this.drawer.querySelector('ozera-cart-protection [role="switch"]');
      if (this.drawer.classList.contains('active') && typeof trapFocus === 'function') {
        trapFocus(this.drawer.querySelector('.drawer__inner'), newToggle || this.drawer.querySelector('.drawer__close'));
      }
      const status = this.drawer.querySelector('[data-protection-status]');
      if (status) status.textContent = enabled && hasMerchandise ? 'Ochrana zásilky byla přidána.' : 'Ochrana zásilky byla odebrána.';
    } catch (error) {
      // A dropped response can follow a successful cart write. Read the real
      // state again before allowing checkout; never show an outdated total.
      try {
        const updated = await this.request('cart/update.js', {
          updates: {}, sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: window.location.pathname,
        });
        if (!updated.sections?.['cart-drawer'] || !updated.sections?.['cart-icon-bubble']) throw new Error();
        this.drawer.classList.toggle('is-empty', updated.item_count === 0);
        this.drawer.renderContents(updated, true);
        const current = this.drawer.querySelector('ozera-cart-protection');
        if (current) {
          current.error.textContent = error.message;
          current.error.hidden = false;
          if (typeof trapFocus === 'function' && this.drawer.classList.contains('active')) trapFocus(this.drawer.querySelector('.drawer__inner'), current.toggle);
        }
      } catch {
        this.error.textContent = 'Košík se nepodařilo aktualizovat. Obnovte prosím stránku před pokračováním k pokladně.';
        this.error.hidden = false;
        this.toggle.disabled = false;
        this.removeAttribute('aria-busy');
        if (checkout) checkout.disabled = true;
      }
    } finally {
      itemControls.forEach(({ element, disabled }) => { element.disabled = disabled; });
      this.drawer.removeAttribute('aria-busy');
      OzeraCartProtection.pending = false;
    }
  }
}

if (!customElements.get('ozera-cart-protection')) customElements.define('ozera-cart-protection', OzeraCartProtection);

// Keep one deadline across drawer re-renders; the display never reserves stock.
class OzeraCartCountdown extends HTMLElement {
  connectedCallback() {
    this.duration = Math.max(0, Number(this.dataset.duration) || 0);
    this.storageKey = `ozera-cart-countdown-${this.duration}`;
    this.drawer = this.closest('cart-drawer');
    this.render();
    this.observer = new MutationObserver(() => this.start());
    this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
    this.start();
  }

  start() {
    if (this.interval || !this.drawer.classList.contains('active')) return;
    let saved;
    try { saved = Number(sessionStorage.getItem(this.storageKey)); } catch { /* Storage may be unavailable. */ }
    const now = Date.now();
    this.deadline = (saved > now && saved) || (OzeraCartCountdown.deadline > now && OzeraCartCountdown.deadline) || now + this.duration * 1000;
    OzeraCartCountdown.deadline = this.deadline;
    try { sessionStorage.setItem(this.storageKey, String(this.deadline)); } catch { /* Keep the in-page deadline. */ }
    this.render();
    if (this.deadline > Date.now()) this.interval = setInterval(() => this.render(), 1000);
  }

  render() {
    const seconds = this.deadline ? Math.max(0, Math.ceil((this.deadline - Date.now()) / 1000)) : this.duration;
    this.textContent = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
    if (seconds === 0 && this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  disconnectedCallback() {
    clearInterval(this.interval);
    this.observer?.disconnect();
  }
}

if (!customElements.get('ozera-cart-countdown')) customElements.define('ozera-cart-countdown', OzeraCartCountdown);
